<template>
    <div class="distributor-management">
      <div class="page-header">
        <div>
          <h2 class="page-title">分销管理</h2>
          <p class="page-desc">管理分销员和佣金设置</p>
        </div>
      </div>
      
      <!-- 统计卡片 -->
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-value">{{ stats.total || 0 }}</div>
          <div class="stat-label">分销员总数</div>
        </div>
        <div class="stat-card success">
          <div class="stat-value">{{ stats.active || 0 }}</div>
          <div class="stat-label">活跃分销员</div>
        </div>
        <div class="stat-card warning">
          <div class="stat-value">¥{{ (stats.total_commission || 0).toFixed(2) }}</div>
          <div class="stat-label">累计佣金</div>
        </div>
        <div class="stat-card primary">
          <div class="stat-value">¥{{ (stats.available_commission || 0).toFixed(2) }}</div>
          <div class="stat-label">待结算佣金</div>
        </div>
      </div>
      
      <!-- Tab切换 -->
      <el-tabs v-model="activeTab" class="distributor-tabs">
        <!-- 分销员列表Tab -->
        <el-tab-pane label="分销员列表" name="distributors">
          <div class="filter-bar">
            <el-input 
              v-model="searchKey" 
              placeholder="搜索昵称/手机号/分销员编号"
              prefix-icon="Search"
              clearable
              style="width: 260px"
              @keyup.enter="fetchDistributors"
            />
            <el-select v-model="statusFilter" placeholder="全部状态" clearable style="width: 120px" @change="fetchDistributors">
              <el-option label="已通过" :value="1" />
              <el-option label="待审核" :value="0" />
              <el-option label="已拒绝" :value="2" />
              <el-option label="已冻结" :value="3" />
            </el-select>
            <el-button type="primary" @click="fetchDistributors">查询</el-button>
            <el-button @click="resetSearch">重置</el-button>
          </div>
          
          <el-table :data="distributorList" border v-loading="distributorLoading">
            <el-table-column label="分销员信息" min-width="200">
              <template #default="{ row }">
                <div class="distributor-info">
                  <el-avatar :size="40" :src="row.avatar_url || defaultAvatar" />
                  <div class="distributor-detail">
                    <div class="distributor-name">{{ row.nickname || '用户' + row.user_id }}</div>
                    <div class="distributor-no">编号：{{ row.distributor_no }}</div>
                    <div class="distributor-phone">{{ row.phone || '未绑定' }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column label="分销等级" width="100" align="center">
              <template #default="{ row }">
                <el-tag :type="getLevelType(row.level)" size="small">
                  {{ getLevelText(row.level) }}
                </el-tag>
              </template>
            </el-table-column>
            
            <el-table-column label="佣金比例" width="100" align="center">
              <template #default="{ row }">
                {{ row.commission_rate }}%
              </template>
            </el-table-column>
            
            <el-table-column label="团队人数" width="100" align="center">
              <template #default="{ row }">
                <div>直接：{{ row.direct_team_count || 0 }}</div>
                <div>总：{{ row.total_team_count || 0 }}</div>
              </template>
            </el-table-column>
            
            <el-table-column label="佣金统计" width="160" align="center">
              <template #default="{ row }">
                <div>累计：¥{{ (row.total_commission || 0).toFixed(2) }}</div>
                <div>已提现：¥{{ (row.withdrawn_commission || 0).toFixed(2) }}</div>
                <div>可提现：¥{{ (row.available_commission || 0).toFixed(2) }}</div>
              </template>
            </el-table-column>
            
            <el-table-column label="状态" width="90" align="center">
              <template #default="{ row }">
                <el-tag :type="getStatusType(row.status)" size="small">
                  {{ getStatusText(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            
            <el-table-column label="申请时间" width="160">
              <template #default="{ row }">
                {{ formatTime(row.apply_time) }}
              </template>
            </el-table-column>
            
            <el-table-column label="操作" width="200" fixed="right" align="center">
              <template #default="{ row }">
                <el-button type="primary" link size="small" @click="viewOrderDetail(row)">
                  <el-icon><Document /></el-icon> 订单记录
                </el-button>
                <el-button type="success" link size="small" @click="openEditDialog(row)">
                  <el-icon><Edit /></el-icon> 编辑
                </el-button>
                <el-button 
                  v-if="row.status === 0"
                  type="success" 
                  link 
                  size="small" 
                  @click="auditDistributor(row, 1)"
                >
                  通过
                </el-button>
                <el-button 
                  v-if="row.status === 0"
                  type="danger" 
                  link 
                  size="small" 
                  @click="auditDistributor(row, 2)"
                >
                  拒绝
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          
          <div class="pagination-wrapper" v-if="distributorPagination.total > distributorPagination.limit">
            <el-pagination
              v-model:current-page="distributorPagination.page"
              v-model:page-size="distributorPagination.limit"
              :total="distributorPagination.total"
              :page-sizes="[10, 20, 50]"
              layout="total, sizes, prev, pager, next"
              @size-change="fetchDistributors"
              @current-change="fetchDistributors"
            />
          </div>
        </el-tab-pane>
        
        <!-- 佣金记录Tab -->
        <el-tab-pane label="佣金记录" name="orders">
          <div class="filter-bar">
            <el-input 
              v-model="orderSearchKey" 
              placeholder="搜索订单号"
              prefix-icon="Search"
              clearable
              style="width: 260px"
              @keyup.enter="fetchOrders"
            />
            <el-select v-model="orderStatusFilter" placeholder="全部状态" clearable style="width: 120px" @change="fetchOrders">
              <el-option label="待结算" :value="0" />
              <el-option label="已结算" :value="1" />
              <el-option label="已失效" :value="2" />
            </el-select>
            <el-button type="primary" @click="fetchOrders">查询</el-button>
            <el-button @click="resetOrderSearch">重置</el-button>
          </div>
          
          <el-table :data="orderList" border v-loading="orderLoading">
            <el-table-column prop="order_no" label="订单号" width="180" />
            <el-table-column prop="product_name" label="商品名称" min-width="200" />
            <el-table-column prop="buyer_id" label="购买用户ID" width="100" />
            <el-table-column label="订单金额" width="120">
              <template #default="{ row }">¥{{ (row.order_amount || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="佣金比例" width="100">
              <template #default="{ row }">{{ row.commission_rate }}%</template>
            </el-table-column>
            <el-table-column label="佣金金额" width="120">
              <template #default="{ row }">¥{{ (row.commission_amount || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="分销层级" width="100">
              <template #default="{ row }">
                {{ row.level === 1 ? '一级分销' : '二级分销' }}
              </template>
            </el-table-column>
            <el-table-column label="状态" width="90">
              <template #default="{ row }">
                <el-tag :type="row.status === 1 ? 'success' : 'warning'" size="small">
                  {{ row.status === 1 ? '已结算' : '待结算' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="create_time" label="下单时间" width="160" />
          </el-table>
          
          <div class="pagination-wrapper" v-if="orderPagination.total > orderPagination.limit">
            <el-pagination
              v-model:current-page="orderPagination.page"
              v-model:page-size="orderPagination.limit"
              :total="orderPagination.total"
              layout="total, prev, pager, next"
              @current-change="fetchOrders"
            />
          </div>
        </el-tab-pane>
        
        <!-- 商品佣金配置Tab -->
        <el-tab-pane label="商品佣金配置" name="products">
          <div class="filter-bar">
            <el-input 
              v-model="productSearchKey" 
              placeholder="搜索商品名称"
              prefix-icon="Search"
              clearable
              style="width: 260px"
            />
            <el-button type="primary" @click="searchProducts">查询</el-button>
            <el-button @click="resetProductSearch">重置</el-button>
          </div>
          
          <el-table :data="filteredProductList" border v-loading="productLoading">
            <el-table-column label="商品信息" min-width="250">
              <template #default="{ row }">
                <div class="product-info">
                  <el-image :src="row.cover_image || defaultImage" fit="cover" class="product-img" />
                  <div class="product-detail">
                    <div class="product-name">{{ row.product_name }}</div>
                    <div class="product-price">售价：¥{{ (row.sale_price || 0).toFixed(2) }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            
            <el-table-column label="一级佣金比例" width="150">
              <template #default="{ row }">
                <el-input-number 
                  v-model="row.commission_rate_1" 
                  :min="0" 
                  :max="100" 
                  :precision="2" 
                  size="small"
                  controls-position="right"
                  style="width: 100%"
                />
              </template>
            </el-table-column>
            
            <el-table-column label="二级佣金比例" width="150">
              <template #default="{ row }">
                <el-input-number 
                  v-model="row.commission_rate_2" 
                  :min="0" 
                  :max="100" 
                  :precision="2" 
                  size="small"
                  controls-position="right"
                  style="width: 100%"
                />
              </template>
            </el-table-column>
            
            <el-table-column label="佣金计算方式" width="130">
              <template #default="{ row }">
                <el-select v-model="row.commission_type" size="small" style="width: 100%">
                  <el-option label="按订单金额" :value="1" />
                  <el-option label="按实付金额" :value="2" />
                </el-select>
              </template>
            </el-table-column>
            
            <el-table-column label="状态" width="90">
              <template #default="{ row }">
                <el-switch v-model="row.is_enabled" :active-value="1" :inactive-value="0" />
              </template>
            </el-table-column>
            
            <el-table-column label="操作" width="100">
              <template #default="{ row }">
                <el-button type="primary" size="small" @click="saveProductConfig(row)" :loading="row.saving">
                  保存
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
        
        <!-- 提现记录Tab -->
        <el-tab-pane label="提现记录" name="withdrawals">
          <div class="filter-bar">
            <el-select v-model="withdrawStatusFilter" placeholder="全部状态" clearable style="width: 120px" @change="fetchWithdrawals">
              <el-option label="待审核" :value="0" />
              <el-option label="审核通过" :value="1" />
              <el-option label="提现中" :value="2" />
              <el-option label="提现成功" :value="3" />
              <el-option label="提现失败" :value="4" />
              <el-option label="已拒绝" :value="5" />
            </el-select>
            <el-button type="primary" @click="fetchWithdrawals">查询</el-button>
            <el-button @click="resetWithdrawSearch">重置</el-button>
          </div>
          
          <el-table :data="withdrawalList" border v-loading="withdrawalLoading">
            <el-table-column prop="withdraw_no" label="提现编号" width="160" />
            <el-table-column prop="distributor_id" label="分销员ID" width="100" />
            <el-table-column label="提现金额" width="120">
              <template #default="{ row }">¥{{ (row.amount || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="手续费" width="100">
              <template #default="{ row }">¥{{ (row.fee || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="实际到账" width="120">
              <template #default="{ row }">¥{{ (row.actual_amount || 0).toFixed(2) }}</template>
            </el-table-column>
            <el-table-column label="提现方式" width="100">
              <template #default="{ row }">
                {{ getWithdrawTypeText(row.withdraw_type) }}
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="getWithdrawStatusType(row.status)" size="small">
                  {{ getWithdrawStatusText(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="create_time" label="申请时间" width="160" />
            <el-table-column label="操作" width="150">
              <template #default="{ row }">
                <el-button 
                  v-if="row.status === 0"
                  type="success" 
                  size="small" 
                  @click="auditWithdrawal(row, 1)"
                >
                  通过
                </el-button>
                <el-button 
                  v-if="row.status === 0"
                  type="danger" 
                  size="small" 
                  @click="auditWithdrawal(row, 5)"
                >
                  拒绝
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          
          <div class="pagination-wrapper" v-if="withdrawalPagination.total > withdrawalPagination.limit">
            <el-pagination
              v-model:current-page="withdrawalPagination.page"
              v-model:page-size="withdrawalPagination.limit"
              :total="withdrawalPagination.total"
              layout="total, prev, pager, next"
              @current-change="fetchWithdrawals"
            />
          </div>
        </el-tab-pane>
      </el-tabs>
      
      <!-- 编辑分销员弹窗 -->
      <el-dialog v-model="editDialogVisible" title="编辑分销员" width="500px">
        <el-form :model="editForm" label-width="100px">
          <el-form-item label="分销等级">
            <el-select v-model="editForm.level" style="width: 100%">
              <el-option label="普通分销员" :value="1" />
              <el-option label="高级分销员" :value="2" />
              <el-option label="金牌分销员" :value="3" />
            </el-select>
          </el-form-item>
          <el-form-item label="佣金比例">
            <el-input-number v-model="editForm.commission_rate" :min="0" :max="100" :precision="2" style="width: 100%" />
            <div class="form-tip">%</div>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="editForm.status" style="width: 100%">
              <el-option label="已通过" :value="1" />
              <el-option label="待审核" :value="0" />
              <el-option label="已拒绝" :value="2" />
              <el-option label="已冻结" :value="3" />
            </el-select>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitEdit" :loading="editLoading">确 定</el-button>
        </template>
      </el-dialog>
      
      <!-- 分销员订单记录弹窗 -->
      <el-dialog v-model="orderDetailDialogVisible" :title="`${currentDistributor?.nickname || '分销员'} - 订单记录`" width="800px">
        <el-table :data="distributorOrders" border v-loading="distributorOrderLoading">
          <el-table-column prop="order_no" label="订单号" width="160" />
          <el-table-column prop="product_name" label="商品名称" min-width="200" />
          <el-table-column label="订单金额" width="100">
            <template #default="{ row }">¥{{ (row.order_amount || 0).toFixed(2) }}</template>
          </el-table-column>
          <el-table-column label="佣金" width="100">
            <template #default="{ row }">¥{{ (row.commission_amount || 0).toFixed(2) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="90">
            <template #default="{ row }">
              <el-tag :type="row.status === 1 ? 'success' : 'warning'" size="small">
                {{ row.status === 1 ? '已结算' : '待结算' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="create_time" label="下单时间" width="160" />
        </el-table>
        <div class="pagination-small" v-if="distributorOrderPagination.total > distributorOrderPagination.limit">
          <el-pagination
            v-model:current-page="distributorOrderPagination.page"
            v-model:page-size="distributorOrderPagination.limit"
            :total="distributorOrderPagination.total"
            layout="prev, pager, next"
            small
            @current-change="loadDistributorOrders"
          />
        </div>
      </el-dialog>
    </div>
  </template>
  
  <script>
  import { ref, reactive, computed, onMounted } from 'vue'
  import { ElMessage, ElLoading } from 'element-plus'
  import Cookies from 'js-cookie'
  import axios from 'axios'
  import config from '@/config'
  import { Document, Edit } from '@element-plus/icons-vue'
  
  export default {
    name: 'DistributorManagementPage',
    components: { Document, Edit },
    setup() {
      const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
      const agentId = ref(userInfo.agent_id || 0)
      const storeId = ref(userInfo.id)
      const defaultImage = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
      const defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png'
      
      const activeTab = ref('distributors')
      
      // 分销员相关
      const distributorLoading = ref(false)
      const distributorList = ref([])
      const stats = ref({})
      const searchKey = ref('')
      const statusFilter = ref(null)
      const distributorPagination = reactive({ page: 1, limit: 10, total: 0 })
      
      // 佣金订单相关
      const orderLoading = ref(false)
      const orderList = ref([])
      const orderSearchKey = ref('')
      const orderStatusFilter = ref(null)
      const orderPagination = reactive({ page: 1, limit: 10, total: 0 })
      
      // 商品配置相关
      const productLoading = ref(false)
      const productList = ref([])
      const productSearchKey = ref('')
      
      // 提现记录相关
      const withdrawalLoading = ref(false)
      const withdrawalList = ref([])
      const withdrawStatusFilter = ref(null)
      const withdrawalPagination = reactive({ page: 1, limit: 10, total: 0 })
      
      // 编辑弹窗
      const editDialogVisible = ref(false)
      const editLoading = ref(false)
      const currentDistributor = ref(null)
      const editForm = reactive({
        id: null,
        level: 1,
        commission_rate: 0,
        status: 1
      })
      
      // 订单详情弹窗
      const orderDetailDialogVisible = ref(false)
      const distributorOrders = ref([])
      const distributorOrderLoading = ref(false)
      const distributorOrderPagination = reactive({ page: 1, limit: 10, total: 0 })
      
      // 计算属性 - 筛选后的商品列表
      const filteredProductList = computed(() => {
        if (!productSearchKey.value) return productList.value
        return productList.value.filter(p => 
          p.product_name && p.product_name.toLowerCase().includes(productSearchKey.value.toLowerCase())
        )
      })
      
      // 获取等级文本
      const getLevelText = (level) => {
        const map = { 1: '普通', 2: '高级', 3: '金牌' }
        return map[level] || '普通'
      }
      
      const getLevelType = (level) => {
        const map = { 1: 'info', 2: 'warning', 3: 'danger' }
        return map[level] || 'info'
      }
      
      // 获取状态文本
      const getStatusText = (status) => {
        const map = { 0: '待审核', 1: '已通过', 2: '已拒绝', 3: '已冻结' }
        return map[status] || '未知'
      }
      
      const getStatusType = (status) => {
        const map = { 0: 'warning', 1: 'success', 2: 'danger', 3: 'danger' }
        return map[status] || 'info'
      }
      
      // 提现方式文本
      const getWithdrawTypeText = (type) => {
        const map = { wechat: '微信', alipay: '支付宝', bank: '银行卡' }
        return map[type] || type
      }
      
      const getWithdrawStatusText = (status) => {
        const map = { 0: '待审核', 1: '审核通过', 2: '提现中', 3: '提现成功', 4: '提现失败', 5: '已拒绝' }
        return map[status] || '未知'
      }
      
      const getWithdrawStatusType = (status) => {
        const map = { 0: 'warning', 1: 'success', 2: 'info', 3: 'success', 4: 'danger', 5: 'danger' }
        return map[status] || 'info'
      }
      
      // 格式化时间
      const formatTime = (time) => {
        if (!time) return '-'
        return new Date(time).toLocaleString('zh-CN')
      }
      
      // 获取分销员列表
      const fetchDistributors = async () => {
        distributorLoading.value = true
        try {
          const params = {
            action: 'distributors',
            agent_id: agentId.value,
            store_id: storeId.value,
            page: distributorPagination.page,
            limit: distributorPagination.limit,
            search: searchKey.value,
            status: statusFilter.value
          }
          
          const response = await axios.get(`${config.BASE_URL}/shop/api/distributor_api.php`, { params })
          
          if (response.data.code === 200) {
            distributorList.value = response.data.data.list
            stats.value = response.data.data.stats
            distributorPagination.total = response.data.data.pagination.total
          }
        } catch (error) {
          console.error('获取分销员失败:', error)
          ElMessage.error('获取数据失败')
        } finally {
          distributorLoading.value = false
        }
      }
      
      // 获取佣金订单
      const fetchOrders = async () => {
        orderLoading.value = true
        try {
          const params = {
            action: 'orders',
            agent_id: agentId.value,
            store_id: storeId.value,
            page: orderPagination.page,
            limit: orderPagination.limit,
            search: orderSearchKey.value,
            status: orderStatusFilter.value
          }
          
          const response = await axios.get(`${config.BASE_URL}/shop/api/distributor_api.php`, { params })
          
          if (response.data.code === 200) {
            orderList.value = response.data.data.list
            orderPagination.total = response.data.data.pagination.total
          }
        } catch (error) {
          console.error('获取订单失败:', error)
          ElMessage.error('获取数据失败')
        } finally {
          orderLoading.value = false
        }
      }
      
      // 获取商品佣金配置
      const fetchProducts = async () => {
        productLoading.value = true
        try {
          const params = {
            action: 'products',
            agent_id: agentId.value,
            store_id: storeId.value
          }
          
          const response = await axios.get(`${config.BASE_URL}/shop/api/distributor_api.php`, { params })
          
          if (response.data.code === 200) {
            productList.value = response.data.data.map(p => ({
              ...p,
              saving: false
            }))
          }
        } catch (error) {
          console.error('获取商品配置失败:', error)
          ElMessage.error('获取数据失败')
        } finally {
          productLoading.value = false
        }
      }
      
      // 获取提现记录
      const fetchWithdrawals = async () => {
        withdrawalLoading.value = true
        try {
          const params = {
            action: 'withdrawals',
            agent_id: agentId.value,
            store_id: storeId.value,
            page: withdrawalPagination.page,
            limit: withdrawalPagination.limit,
            status: withdrawStatusFilter.value
          }
          
          const response = await axios.get(`${config.BASE_URL}/shop/api/distributor_api.php`, { params })
          
          if (response.data.code === 200) {
            withdrawalList.value = response.data.data.list
            withdrawalPagination.total = response.data.data.pagination.total
          }
        } catch (error) {
          console.error('获取提现记录失败:', error)
          ElMessage.error('获取数据失败')
        } finally {
          withdrawalLoading.value = false
        }
      }
      
      // 加载分销员订单
      const loadDistributorOrders = async () => {
        if (!currentDistributor.value) return
        
        distributorOrderLoading.value = true
        try {
          const params = {
            action: 'orders',
            agent_id: agentId.value,
            store_id: storeId.value,
            distributor_id: currentDistributor.value.id,
            page: distributorOrderPagination.page,
            limit: distributorOrderPagination.limit
          }
          
          const response = await axios.get(`${config.BASE_URL}/shop/api/distributor_api.php`, { params })
          
          if (response.data.code === 200) {
            distributorOrders.value = response.data.data.list
            distributorOrderPagination.total = response.data.data.pagination.total
          }
        } catch (error) {
          console.error('获取分销员订单失败:', error)
          ElMessage.error('获取数据失败')
        } finally {
          distributorOrderLoading.value = false
        }
      }
      
      // 重置搜索
      const resetSearch = () => {
        searchKey.value = ''
        statusFilter.value = null
        distributorPagination.page = 1
        fetchDistributors()
      }
      
      const resetOrderSearch = () => {
        orderSearchKey.value = ''
        orderStatusFilter.value = null
        orderPagination.page = 1
        fetchOrders()
      }
      
      const resetProductSearch = () => {
        productSearchKey.value = ''
      }
      
      const searchProducts = () => {
        // computed 会自动处理筛选
      }
      
      const resetWithdrawSearch = () => {
        withdrawStatusFilter.value = null
        withdrawalPagination.page = 1
        fetchWithdrawals()
      }
      
      // 打开编辑弹窗
      const openEditDialog = (row) => {
        currentDistributor.value = row
        editForm.id = row.id
        editForm.level = row.level
        editForm.commission_rate = row.commission_rate
        editForm.status = row.status
        editDialogVisible.value = true
      }
      
      // 提交编辑
      const submitEdit = async () => {
        editLoading.value = true
        try {
          const response = await axios.put(`${config.BASE_URL}/shop/api/distributor_api.php`, {
            id: editForm.id,
            store_id: storeId.value,
            level: editForm.level,
            commission_rate: editForm.commission_rate,
            status: editForm.status
          })
          
          if (response.data.code === 200) {
            ElMessage.success('更新成功')
            editDialogVisible.value = false
            fetchDistributors()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('更新失败:', error)
          ElMessage.error('操作失败')
        } finally {
          editLoading.value = false
        }
      }
      
      // 审核分销员
      const auditDistributor = async (row, status) => {
        const action = status === 1 ? '通过' : '拒绝'
        const loadingInstance = ElLoading.service({ text: `${action}中...` })
        try {
          const response = await axios.put(`${config.BASE_URL}/shop/api/distributor_api.php`, {
            id: row.id,
            store_id: storeId.value,
            status: status
          })
          
          if (response.data.code === 200) {
            ElMessage.success(`${action}成功`)
            fetchDistributors()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('审核失败:', error)
          ElMessage.error('操作失败')
        } finally {
          loadingInstance.close()
        }
      }
      
      // 查看订单详情
      const viewOrderDetail = async (row) => {
        currentDistributor.value = row
        distributorOrderPagination.page = 1
        orderDetailDialogVisible.value = true
        await loadDistributorOrders()
      }
      
      // 保存商品佣金配置
      const saveProductConfig = async (row) => {
        row.saving = true
        try {
          const response = await axios.post(`${config.BASE_URL}/shop/api/distributor_api.php`, {
            action: 'product_config',
            agent_id: agentId.value,
            store_id: storeId.value,
            product_id: row.product_id,
            commission_rate_1: row.commission_rate_1,
            commission_rate_2: row.commission_rate_2,
            commission_type: row.commission_type,
            is_enabled: row.is_enabled
          })
          
          if (response.data.code === 200) {
            ElMessage.success('保存成功')
          } else {
            ElMessage.error(response.data.message || '保存失败')
          }
        } catch (error) {
          console.error('保存失败:', error)
          ElMessage.error('保存失败')
        } finally {
          row.saving = false
        }
      }
      
      // 审核提现
      const auditWithdrawal = async (row, status) => {
        const action = status === 1 ? '通过' : '拒绝'
        const loadingInstance = ElLoading.service({ text: `${action}中...` })
        try {
          const response = await axios.post(`${config.BASE_URL}/shop/api/distributor_api.php`, {
            action: 'withdraw_audit',
            id: row.id,
            store_id: storeId.value,
            status: status,
            audit_remark: status === 5 ? '管理员拒绝' : null
          })
          
          if (response.data.code === 200) {
            ElMessage.success(`${action}成功`)
            fetchWithdrawals()
          } else {
            ElMessage.error(response.data.message || '操作失败')
          }
        } catch (error) {
          console.error('审核失败:', error)
          ElMessage.error('操作失败')
        } finally {
          loadingInstance.close()
        }
      }
      
      onMounted(() => {
        fetchDistributors()
        fetchOrders()
        fetchProducts()
        fetchWithdrawals()
      })
      
      return {
        activeTab,
        defaultImage,
        defaultAvatar,
        // 分销员
        distributorLoading,
        distributorList,
        stats,
        searchKey,
        statusFilter,
        distributorPagination,
        // 订单
        orderLoading,
        orderList,
        orderSearchKey,
        orderStatusFilter,
        orderPagination,
        // 商品
        productLoading,
        productList,
        productSearchKey,
        filteredProductList,
        // 提现
        withdrawalLoading,
        withdrawalList,
        withdrawStatusFilter,
        withdrawalPagination,
        // 编辑弹窗
        editDialogVisible,
        editLoading,
        editForm,
        // 订单详情弹窗
        orderDetailDialogVisible,
        distributorOrders,
        distributorOrderLoading,
        distributorOrderPagination,
        // 方法
        getLevelText,
        getLevelType,
        getStatusText,
        getStatusType,
        getWithdrawTypeText,
        getWithdrawStatusText,
        getWithdrawStatusType,
        formatTime,
        resetSearch,
        resetOrderSearch,
        resetProductSearch,
        searchProducts,
        resetWithdrawSearch,
        openEditDialog,
        submitEdit,
        auditDistributor,
        viewOrderDetail,
        saveProductConfig,
        auditWithdrawal,
        fetchDistributors,
        fetchOrders,
        fetchWithdrawals,
        loadDistributorOrders
      }
    }
  }
  </script>
  
  <style scoped lang="scss">
  .distributor-management {
    .page-header {
      margin-bottom: 24px;
      
      .page-title {
        font-size: 24px;
        font-weight: 600;
        color: #1a1a2e;
        margin-bottom: 4px;
      }
      
      .page-desc {
        font-size: 14px;
        color: #8c8c8c;
      }
    }
    
    .stat-cards {
      display: flex;
      gap: 16px;
      margin-bottom: 24px;
      flex-wrap: wrap;
      
      .stat-card {
        flex: 1;
        min-width: 120px;
        background: #fff;
        border-radius: 12px;
        padding: 16px 20px;
        text-align: center;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        
        &.success .stat-value { color: #67c23a; }
        &.warning .stat-value { color: #e6a23c; }
        &.primary .stat-value { color: #409eff; }
        
        .stat-value {
          font-size: 28px;
          font-weight: 700;
        }
        
        .stat-label {
          font-size: 13px;
          color: #8c8c8c;
          margin-top: 4px;
        }
      }
    }
    
    .distributor-tabs {
      :deep(.el-tabs__header) {
        margin-bottom: 20px;
      }
    }
    
    .filter-bar {
      display: flex;
      gap: 12px;
      flex-wrap: wrap;
      margin-bottom: 20px;
    }
    
    .distributor-info {
      display: flex;
      gap: 12px;
      align-items: center;
      
      .distributor-detail {
        .distributor-name {
          font-weight: 500;
          margin-bottom: 2px;
        }
        
        .distributor-no, .distributor-phone {
          font-size: 12px;
          color: #8c8c8c;
        }
      }
    }
    
    .product-info {
      display: flex;
      gap: 12px;
      align-items: center;
      
      .product-img {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        object-fit: cover;
      }
      
      .product-detail {
        .product-name {
          font-weight: 500;
          margin-bottom: 4px;
        }
        
        .product-price {
          font-size: 12px;
          color: #f56c6c;
        }
      }
    }
    
    .pagination-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-top: 20px;
    }
    
    .pagination-small {
      display: flex;
      justify-content: flex-end;
      margin-top: 16px;
    }
    
    .form-tip {
      font-size: 12px;
      color: #8c8c8c;
      margin-top: 4px;
    }
  }
  </style>